import {
  resolveDispatchAlertCaptureOperationKey,
  scoreDispatchAlertCaptureContext,
} from "./dispatchAlertCaptureContext.js";

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

const DEFAULT_INCIDENT_GROUP_WINDOW_MS = 5_000;
const CONTAINED_INCIDENT_TEXT_MIN_LENGTH = 24;

function normalizeIncidentText(value) {
  return normalizeText(value)
    .normalize("NFKC")
    .toLocaleLowerCase("fr-CA")
    .replace(/[\s\p{P}\p{S}]+/gu, "");
}

function resolveCaptureTimestamp(capture) {
  const rawTimestamp = normalizeText(capture?.occurred_at) || normalizeText(capture?.created_at);
  if (!rawTimestamp) {
    return null;
  }
  const timestamp = new Date(rawTimestamp).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function buildCaptureIncidentScope(capture) {
  return [
    normalizeText(capture?.user_id) || normalizeText(capture?.user_email),
    normalizeText(capture?.campaign_id),
    normalizeText(capture?.dispatch_date),
    normalizeText(capture?.dispatch_period),
    resolveDispatchAlertCaptureOperationKey(capture?.capture_context),
  ].join("\u0000");
}

function incidentTextsMatch(leftText, rightText) {
  if (!leftText || !rightText) {
    return false;
  }
  if (leftText === rightText) {
    return true;
  }
  const shortestLength = Math.min(leftText.length, rightText.length);
  return shortestLength >= CONTAINED_INCIDENT_TEXT_MIN_LENGTH
    && (leftText.includes(rightText) || rightText.includes(leftText));
}

function captureSurfaceSize(capture) {
  const width = Number(capture?.width);
  const height = Number(capture?.height);
  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return width * height;
  }
  const sizeBytes = Number(capture?.size_bytes);
  return Number.isFinite(sizeBytes) && sizeBytes > 0 ? sizeBytes : Number.POSITIVE_INFINITY;
}

function compareCapturePreference(leftCapture, rightCapture) {
  const leftTypePriority = leftCapture?.alert_type === "global_error" ? 0 : 1;
  const rightTypePriority = rightCapture?.alert_type === "global_error" ? 0 : 1;
  if (leftTypePriority !== rightTypePriority) {
    return leftTypePriority - rightTypePriority;
  }
  return captureSurfaceSize(leftCapture) - captureSurfaceSize(rightCapture);
}

export function groupDispatchAlertCaptures(captures = [], {
  windowMs = DEFAULT_INCIDENT_GROUP_WINDOW_MS,
} = {}) {
  const normalizedWindowMs = Number.isFinite(Number(windowMs))
    ? Math.max(0, Number(windowMs))
    : DEFAULT_INCIDENT_GROUP_WINDOW_MS;
  const groups = [];
  const capturesNewestFirst = captures
    .map((capture, inputIndex) => ({
      capture,
      inputIndex,
      timestamp: resolveCaptureTimestamp(capture),
    }))
    .sort((left, right) => {
      if (left.timestamp == null && right.timestamp == null) {
        return left.inputIndex - right.inputIndex;
      }
      if (left.timestamp == null) {
        return 1;
      }
      if (right.timestamp == null) {
        return -1;
      }
      return right.timestamp - left.timestamp || left.inputIndex - right.inputIndex;
    });

  capturesNewestFirst.forEach(({ capture, timestamp }) => {
    const incidentText = normalizeIncidentText(capture?.alert_text);
    const scope = buildCaptureIncidentScope(capture);
    const matchingGroup = timestamp == null
      ? null
      : groups.find((group) => (
        group.scope === scope
        && Math.abs(group.anchorTimestamp - timestamp) <= normalizedWindowMs
        && incidentTextsMatch(group.incidentText, incidentText)
      ));

    if (!matchingGroup) {
      groups.push({
        anchorTimestamp: timestamp,
        capture,
        count: 1,
        incidentText,
        richestCaptureContext: capture?.capture_context || null,
        richestCaptureContextScore: scoreDispatchAlertCaptureContext(capture?.capture_context),
        scope,
      });
      return;
    }

    matchingGroup.count += 1;
    const candidateContextScore = scoreDispatchAlertCaptureContext(capture?.capture_context);
    if (candidateContextScore > matchingGroup.richestCaptureContextScore) {
      matchingGroup.richestCaptureContext = capture.capture_context;
      matchingGroup.richestCaptureContextScore = candidateContextScore;
    }
    if (compareCapturePreference(capture, matchingGroup.capture) < 0) {
      matchingGroup.capture = capture;
    }
  });

  return groups.map((group) => ({
    ...group.capture,
    capture_context: group.richestCaptureContext || group.capture?.capture_context || null,
    grouped_capture_count: group.count,
  }));
}

export function normalizeCaptureFacetCampaignIds(payload) {
  const campaignIds = Array.isArray(payload?.facets?.campaign_ids)
    ? payload.facets.campaign_ids
    : [];

  return [...new Set(campaignIds.map(normalizeText).filter(Boolean))];
}

export function normalizeCaptureFacetUsers(payload) {
  const users = Array.isArray(payload?.facets?.users)
    ? payload.facets.users
    : [];
  const usersById = new Map();

  users.forEach((user) => {
    const userId = normalizeText(user?.user_id);
    if (!userId || usersById.has(userId)) {
      return;
    }
    const email = normalizeText(user?.user_email);
    usersById.set(userId, {
      email,
      label: email || userId,
      userId,
    });
  });

  return Array.from(usersById.values());
}

export function buildCaptureCampaignFilterOptions(payload, captures = []) {
  const campaignIds = normalizeCaptureFacetCampaignIds(payload);
  const seenCampaignIds = new Set(campaignIds);

  captures.forEach((capture) => {
    const campaignId = normalizeText(capture?.campaign_id);
    if (campaignId && !seenCampaignIds.has(campaignId)) {
      seenCampaignIds.add(campaignId);
      campaignIds.push(campaignId);
    }
  });

  return campaignIds;
}

export function buildCaptureUserFilterOptions({
  captures = [],
  facetUsers = [],
  targetUsers = [],
} = {}) {
  const optionsById = new Map();

  const addOption = (user) => {
    const userId = normalizeText(user?.userId ?? user?.user_id);
    if (!userId) {
      return;
    }
    const email = normalizeText(user?.email ?? user?.user_email);
    const label = normalizeText(user?.label ?? user?.display_name) || email || userId;
    const existing = optionsById.get(userId);
    const resolvedLabel = email && email !== label ? `${label} · ${email}` : label;

    if (!existing || existing.label === existing.userId) {
      optionsById.set(userId, {
        label: resolvedLabel,
        userId,
      });
    }
  };

  targetUsers.forEach(addOption);
  facetUsers.forEach(addOption);
  captures.forEach((capture) => addOption({
    userId: capture?.user_id,
    user_email: capture?.user_email,
  }));

  return Array.from(optionsById.values()).sort((left, right) => (
    left.label.localeCompare(right.label, "fr-CA", { sensitivity: "base" })
  ));
}
